import * as React from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-yup";
import * as Yup from "yup";

import { EmailNotificationTypes, Form, VolunteerSearchLink } from "@skv/core";
import { useAppSettings, usePageEnvelope } from "@skv/state";

const SCHEMA = Yup.object({
  email: Yup.string().email().label("Email").required(),
}).defined();

const Unsubscribe = () => {
  const { pageEnvelope } = usePageEnvelope<{
    email: string;
    notificationType: EmailNotificationTypes;
    searchTitle: string;
  }>();

  const { seekVolunteerUrl } = useAppSettings();

  const { data, errorCode } = pageEnvelope;

  const formRef = React.useRef<HTMLFormElement>(null);

  const { email, notificationType, searchTitle } = data || {};

  const { field, values, touched, errors, isSubmitting, createSubmitHandler } =
    useForm({
      validationSchema: SCHEMA,
      defaultErrors: () => {
        const email =
          "We could not unsubscribe you at this time. Please try again.";
        return errorCode ? { email } : {};
      },
      defaultTouched: errorCode
        ? {
            email: true,
          }
        : {},
    });

  const handleSubmit = React.useMemo(() => {
    return createSubmitHandler(() => {
      formRef.current.submit();
    });
  }, [createSubmitHandler]);

  return (
    <>
      <Helmet>
        <title>Unsubscribe</title>
      </Helmet>
      <div className="py-4">
        <VolunteerSearchLink
          className="btn btn-back mb-4"
          to="/volunteering"
          searchType="opp"
          searchQuery={{}}
        >
          Find opportunities
        </VolunteerSearchLink>

        <div className="row">
          <div className="col-md-7">
            {((): React.ReactNode => {
              if (data) {
                let notificationTypeValue;

                switch (notificationType) {
                  case EmailNotificationTypes.OrganisationSummary:
                    notificationTypeValue = "Organisation Summary emails";
                    break;
                  case EmailNotificationTypes.VolunteerOrganisationViewedEoi:
                    notificationTypeValue = "Viewed Application emails";
                    break;
                  case EmailNotificationTypes.VolunteerOpportunitySearch:
                    notificationTypeValue = searchTitle + " opportunity emails";
                    break;
                  case EmailNotificationTypes.VolunteerAllOpportunitySearches:
                    notificationTypeValue = "new opportunity emails";
                    break;
                  default:
                    notificationTypeValue = "new emails";
                    break;
                }

                return (
                  <>
                    <h1>Done!</h1>
                    <p>
                      We have unsubscribed <strong>{email}</strong> from all{" "}
                      {notificationTypeValue}.
                    </p>
                  </>
                );
              }

              return (
                <>
                  <h1>Unsubscribe</h1>
                  <p>
                    Enter your email address below to stop receiving new
                    opportunities via email.
                  </p>

                  <form
                    ref={formRef}
                    method="POST"
                    action="/volunteering/unsubscribe"
                    onSubmit={handleSubmit}
                  >
                    <Form.PureField
                      {...field}
                      id="email-unsubscribe"
                      label="Email address"
                      labelProps={{
                        className: "d-block",
                      }}
                      name="email"
                      type="email"
                      value={values.email || ""}
                      error={errors.email}
                      touched={touched.email}
                    />

                    <p className="small text-muted mt-3 mb-2">
                      By unsubscribing, you agree to our{" "}
                      <a
                        className="text-muted"
                        href={seekVolunteerUrl + "/legal/privacy-statement"}
                      >
                        privacy statement
                      </a>
                      .
                    </p>
                    <button
                      className="btn btn-primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Unsubscribing..." : "Unsubscribe"}
                    </button>
                  </form>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </>
  );
};

export default Unsubscribe;
